/**
 * Centralized Axios Instance with Request/Response Interceptors
 *
 * This file creates a shared axios instance that all service files will use.
 * It automatically adds cache-busting to all GET requests to prevent browser caching issues.
 *
 * @module services/axiosInstance
 */

import axios from 'axios'
import constants from '../constants'
import { isAuthErrorCode } from '../helpers'
import { clearSession, getAuthHeader, getAuthToken } from '../utils/authSession'

/**
 * Create a custom axios instance
 * This is identical to the default axios, but allows us to add interceptors
 * that will apply to ALL requests made through this instance
 */
const axiosInstance = axios.create()

// mgmt-API routes that never carried a token before central injection. Left bare so a
// stale token can't reach a public handler, and a failed login can't look like a lost session.
const PUBLIC_MGMT_PATHS = [
  'login',
  'register',
  'reset/password',
  'verify/email',
  'collaborate/app',
  'customer/context',
  'oauth/',
]

function isAuthenticatedMgmtApiUrl(url) {
  if (!url || !url.startsWith(constants.customerDomain)) return false
  const path = url.slice(constants.customerDomain.length).replace(/^\/+/, '')
  return !PUBLIC_MGMT_PATHS.some(prefix => path.startsWith(prefix))
}

// Registered once at boot (src/index.js) so this module never imports the redux store.
let onSessionRejected = null
export function setSessionRejectedHandler(handler) {
  onSessionRejected = handler
}

// True only when the mgmt API rejected the token we currently hold — a bad token
// passed explicitly (e.g. a stale magic link) must not log the user out.
function isStoredSessionRejected(error, errorCode) {
  if (!isAuthenticatedMgmtApiUrl(error?.config?.url)) return false
  if (!isAuthErrorCode(errorCode)) return false
  const token = getAuthToken()
  return Boolean(token) && error.config.headers?.Authorization === `Bearer ${token}`
}

// The code is passed in because a `responseType: 'blob'` response hides the JSON
// envelope from the interceptor; such callers parse it and hand the code back here.
export function handleSessionRejection(error, errorCode) {
  if (!isStoredSessionRejected(error, errorCode)) return false
  clearSession()
  if (onSessionRejected) onSessionRejected()
  return true
}

axiosInstance.interceptors.request.use(
  config => {
    /**
     * This function runs before EVERY request
     * For GET requests, it adds cache-busting mechanisms to prevent stale data
     *
     * @param {Object} config - The axios request configuration
     * @returns {Object} Modified config that will be used for the request
     */

    // Auth is attached here, once, for the mgmt API only — never for Stripe, Google, etc.
    // An explicit Authorization (e.g. a token that isn't stored yet) always wins.
    const hasAuthHeader = Boolean(config.headers && config.headers.Authorization)
    if (isAuthenticatedMgmtApiUrl(config.url) && !hasAuthHeader) {
      config.headers = { ...config.headers, ...getAuthHeader() }
    }

    // Only apply cache-busting to GET requests
    const isGet = config.method === 'get' || config.method === 'GET'

    // Only touch URLs that look like cometchat
    // This protects Google, Stripe, etc.
    const isInternal =
      config.url && (config.url.includes('cometchat') || config.url.includes('cc-cluster'))

    // Exclude endpoints that don't accept cache-busting parameters
    const isExcludedEndpoint = config.url && config.url.includes('/vcb/builders')

    if (isGet && isInternal && !isExcludedEndpoint) {
      /**
       * Add cache-busting mechanisms:
       */

      // METHOD 1: Add timestamp to query params
      // This changes the URL, forcing browsers to treat it as a new request
      config.params = {
        ...config.params, // Preserve existing params (important!)
        _: Date.now(), // Add timestamp cache-buster
      }

      // // METHOD 2: Add cache-control headers
      // // This tells browsers and proxies not to cache the response
      config.headers = {
        ...config.headers, // Preserve existing headers
        'Cache-Control': 'no-cache, no-store, must-revalidate',
      }
    }

    return config
  },
  error => {
    console.error('Request interceptor error:', error)
    return Promise.reject(error)
  },
)

// RESPONSE INTERCEPTOR - Runs AFTER every response is received
axiosInstance.interceptors.response.use(
  response => {
    return response
  },
  error => {
    handleSessionRejection(error, error?.response?.data?.error?.code)
    return Promise.reject(error)
  },
)

export default axiosInstance
